/**
 * Замок экрана: если пин задан, приложение просит его при открытии.
 *
 * Область действия честно ограничена: это ЗАМОК ЭКРАНА, а не контроль доступа.
 * Токен сессии остаётся валидным, и данные всё равно приходят по нему — пин
 * закрывает содержимое от чужих глаз на разблокированном телефоне, не более.
 * Поэтому проверка идёт запросом на сервер (там же рейт-лимит на попытки), а
 * состояние «снято» живёт в sessionStorage: при закрытии WebView оно исчезает,
 * и в следующий раз пин спросят снова, но навигация и перерисовки не тревожат.
 *
 * Здесь же — разовое предложение задать пин тем, у кого его нет: показывается
 * один раз, отказ запоминается в localStorage.
 */

import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { qk, useVerifyPin } from '@/shared/api/queries';
import { api, errorCodeOf } from '@/shared/api/client';
import type { Me } from '@/shared/api/types';
import { useAuth } from '@/shared/hooks/useAuth';
import { isUnlocked, markUnlocked, subscribeLock } from '@/shared/lib/pinLock';
import { Button, Ceremony, Modal, PinPad } from '@/shared/ui';
import { LanguageSwitch } from '@/shared/ui/LanguageSwitch/LanguageSwitch';
import { ThemeSwitcher } from '@/shared/ui/ThemeSwitcher';

import styles from './PinGate.module.css';

const OFFERED_KEY = 'sg.pin.offered';
const PIN_LENGTH = 4;

function readFlag(storage: Storage, key: string): boolean {
  try {
    return storage.getItem(key) === '1';
  } catch {
    // Приватный режим или отключённый storage: предложение покажется снова.
    return false;
  }
}

function writeFlag(storage: Storage, key: string): void {
  try {
    storage.setItem(key, '1');
  } catch {
    /* no-op */
  }
}

export function PinGate({
  children,
  offerSetup = true,
}: {
  children: ReactNode;
  /** До принятия рисков предлагаем только разблокировку уже заданного PIN. */
  offerSetup?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, setMe } = useAuth();
  const queryClient = useQueryClient();
  const verifyPin = useVerifyPin();

  /**
   * Состояние замка читаем прямо из внешнего хранилища.
   *
   * Через `useState` + подписка это работало ненадёжно: значение копировалось в
   * состояние компонента, и любое несовпадение по времени (перемонтирование
   * `PinGate` при смене маршрута, две копии гейта — клиентская и админская)
   * оставляло копию устаревшей. `useSyncExternalStore` — ровно для этого: одна
   * истина в хранилище, перерисовка по факту изменения.
   */
  const unlocked = useSyncExternalStore(subscribeLock, isUnlocked);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string>();
  const [offerDismissed, setOfferDismissed] = useState(() => readFlag(localStorage, OFFERED_KEY));

  const pinSet = me?.pin_set === true;

  const handleComplete = (value: string): void => {
    setError(undefined);
    verifyPin.mutate(value, {
      onSuccess: () => {
        markUnlocked();
        setPin('');
      },
      onError: async (err) => {
        setPin('');
        try {
          // Staff may have removed a forgotten PIN while this screen stayed open.
          const fresh = await queryClient.fetchQuery({
            queryKey: qk.me,
            queryFn: ({ signal }) => api.get<Me>('/client/me', { signal }),
            staleTime: 0,
          });
          if (fresh.pin_set === false) {
            markUnlocked();
            setMe(fresh);
            setError(undefined);
            return;
          }
        } catch {
          // A failed profile refresh must never bypass an existing screen PIN.
        }
        setError(t(`errors.${errorCodeOf(err)}`, { defaultValue: t('pin.wrong') }));
      },
    });
  };

  if (pinSet && !unlocked) {
    return (
      <div className={styles.lock}>
        <div className={styles.appearanceControls}>
          <LanguageSwitch />
          <ThemeSwitcher />
        </div>
        <div className={styles.lockBody}>
          <div className={styles.panel}>
            <Ceremony tone="gold" className={styles.brand}>
              {t('brand.name')}
            </Ceremony>
            <PinPad
              value={pin}
              onChange={setPin}
              length={PIN_LENGTH}
              title={t('pin.enter')}
              hint={t('pin.enter_hint')}
              error={error}
              onComplete={handleComplete}
              disabled={verifyPin.isPending}
              className={styles.pad}
            />
          </div>
        </div>
      </div>
    );
  }

  const showOffer = offerSetup && me !== null && !pinSet && !offerDismissed;

  const dismissOffer = (): void => {
    writeFlag(localStorage, OFFERED_KEY);
    setOfferDismissed(true);
  };

  return (
    <>
      {children}
      <Modal open={showOffer} onClose={dismissOffer} title={t('pin.offer_title')}>
        <div className={styles.offer}>
          <p className={styles.offerText}>{t('pin.offer_body')}</p>
          <Button
            variant="gold"
            onClick={() => {
              dismissOffer();
              navigate('/profile');
            }}
          >
            {t('pin.offer_set')}
          </Button>
          <Button variant="ghost" onClick={dismissOffer}>
            {t('pin.offer_later')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
